import React, { useEffect, useRef, useState } from 'react'
import './submit-code.styles.scss';
import { switchPlayerMenu } from '../side-menu.component';
import { UseCloudGameTransactions } from '@/utils/firebase.utils';
import { useSelector } from 'react-redux';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import { ModalAlert } from '@/components/modal/modal.component';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import FoundCodeItem from './found-code-item.component';
import FullPageLoader from '@/components/loader/full-page-loader.component';
import { getGameActivityFromDb } from '@/utils/getDataFromDb';
import { Route, Routes, useLocation } from 'react-router-dom';
import ElementView from '../player-blocks/components/status-elements/element-view.component';
import TransactionView from '@/components/SHARED/transaction-view/transaction-view.component';
import useAnalytics from '@/utils/useAnalytics';
import { gameActivityComplete } from '@/components/PREVIEW/previewGameActivity';

const SubmitCode = ({ 
    gameData, 
    playerData, 
    gameElements 
}) => {

    const userProfile = useSelector(state => state.userData.userProfile);
    const screenSize = useSelector(state => state.contentData.screenSize);
    const [ loading, setLoading ] = useState(false);
    const [ alertMessage, setAlertMessage ] = useState(null);
    const [ activityToView, setActivityToView ] = useState(null);
    const [ indActivityData, setIndActivityData ] = useState(null);
    const { trackEvent, trackException } = useAnalytics();
    const activityRef = useRef();
    const location = useLocation();

    useEffect(() => {
        const pageTitle = document.getElementById('page-title');
        if (pageTitle) {
            pageTitle.innerText = 'Secret Codes'
        }
        switchPlayerMenu('secret-btn');
        getFoundCodes();
    }, []);

    function getFoundCodes() {
        if (location.pathname.includes('/preview/player/')) {
            const previewActivity = Object.values(gameActivityComplete)
            .filter(a => a.type === 'secretCode' && a.playerId === playerData.playerId);
            const activityObj = {};
            previewActivity.forEach(a => {
                activityObj[a.id] = a;
            });
            activityRef.current = {
                ...activityRef.current,
                ...activityObj
            };
            setActivityToView(Object.values(activityRef.current));
            return;
        }
        getGameActivityFromDb({
            "gameId": gameData.gameId,
            "playerId": playerData.playerId,
            "activityType": "secretCode",
            "once": true,
            "callback": callback,
        });
        function callback(data) {
            // console.log(data);
            activityRef.current = {
                ...activityRef.current,
                ...data
            }
            setActivityToView(Object.values(activityRef.current));
        }
    }

    async function submitCode() {
        if (location.pathname.includes('/preview/player/')) {
            setAlertMessage('This is a preview of how the secret code submission process ' + 
                'will look for players. In the live game, once you enter a code and click "Submit", ' +
                'your code will be processed and you will receive any rewards or items ' +
                'associated with it. If there are any issues with processing your code, ' +
                'you will receive an alert message with more details.');
            return;
        }

        setLoading(true);
        const code = document.getElementById('code-input').value;
        const formattedCode = code.toUpperCase().replace(/\s/gi,'');
        const res = await UseCloudGameTransactions('checkCode', {
            'code': formattedCode, 
            'gameId': gameData.gameId, 
            'playerId': playerData.playerId, 
            'playerData': userProfile, 
            'gameElements': gameElements, 
            'ts': new Date().getTime()
        })
        // console.log(res);
        setLoading(false);
        if (res.error) {
            setAlertMessage('Something went wrong. Please try again soon. (Error: ' + res.error + ')');
            trackException(`Submit Secret Code Error: ${res.error}`);
            return;
        } else if (res.text && res.text === 'code not found') {
            setAlertMessage('Sorry, that code is incorrect. Please try a different code.');
            trackEvent(
                'submit_secret_code_invalid',
                'Player',
                `Player submitted an invalid secret code "${formattedCode}" in game ${gameData.gameId}`,
                1,
                null
            )
            return;
        } else if (res.text && res.text === 'no access') {
            setAlertMessage('Sorry, that code is not available. Please try a different code.');
            trackEvent(
                'submit_secret_code_no_access',
                'Player',
                `Player submitted a secret code without access "${formattedCode}" in game ${gameData.gameId}`,
                1,
                null
            )
            return;
        } else if (res.text && res.text === 'code already found') {
            setAlertMessage("Sorry, you've already found that code. Please try a different code.");
            trackEvent(
                'submit_secret_code_already_found',
                'Player',
                `Player submitted a secret code they already found "${formattedCode}" in game ${gameData.gameId}`,
                1,
                null
            )
            return;
        } else if (res.text && res.text === 'code limit reached') {
            setAlertMessage("Sorry, that code can no longer be redeemed. It has reached the limit set by your game manager.")
            trackEvent(
                'submit_secret_code_limit_reached',
                'Player',
                `Player submitted a secret code that reached limit "${formattedCode}" in game ${gameData.gameId}`,
                1,
                null
            )
            return;
        } else if (res.success) {
            // setAlertMessage("CONGRATS! You've found a secret code! Check your rewards to see what you earned.")
            trackEvent(
                'submit_secret_code_success',
                'Player',
                `Player successfully submitted secret code "${formattedCode}" in game ${gameData.gameId}`,
                1,
                null
            )
            return;
        }
    }

    return (
        <div className='submit-code-container'>
            <Routes>
                <Route path='' element={
                    <div className='submit-code-content'>
                        <div className='g-card'>
                            <div className='player-title'>
                                <ImageContainer 
                                    src='/images/icons/secret.png' 
                                    alt='secret codes icon' 
                                    className='player-title-icon' />
                                Secret Codes
                            </div>
                            <hr />
                            <div className='secret-card'>
                                <div className='code-input'>
                                    <div className='input field'>
                                        <label>Submit a Secret Code!</label>
                                        <input 
                                            type='text' 
                                            id='code-input' />
                                        {
                                            (loading)
                                            ?
                                            <div className='buttons'>
                                                <button className='g-button' >
                                                    <ThreeDotsLoader />
                                                </button>
                                            </div>
                                            :
                                            <div className='buttons'>
                                                <button 
                                                    className={`g-button primary ${screenSize === 'mobile' ? 'med-btn' : ''}`} 
                                                    onClick={() => submitCode()}>
                                                    Submit
                                                </button>
                                            </div>
                                        }
                                    </div>
                                </div>
                            </div>
                            <div className="g-space-1"></div>
                            <h3>Found Secret Codes</h3>
                            <hr />
                            <div className='found-codes-list'>
                            {
                                (activityToView) &&
                                activityToView
                                .sort((a,b) => a.ts < b.ts ? 1 : -1)
                                .map(a => (
                                    <div key={a.id} >
                                        <FoundCodeItem 
                                            codeInfo={a}
                                            setIndActivityData={setIndActivityData} />
                                    </div>
                                ))
                            }
                            </div>
                        </div>
                    </div>
                }></Route>
                <Route path=':transPath/*' element={
                    (gameData && indActivityData)
                    ?
                    <div className='g-card'>
                        <TransactionView 
                            playerData={playerData} 
                            gameElements={gameElements}
                            playerList={{[playerData.playerId]: playerData}}
                            hidePlayer={true}
                            activityToDisplay={[indActivityData]}
                        />
                    </div>
                    :
                    <div></div>
                }></Route>
            </Routes>
                       
            <ModalAlert 
                show={alertMessage} 
                cancel={() => setAlertMessage(null)} 
                message={alertMessage} /> 
            <FullPageLoader show={loading} />
        </div>
    )
}

export default SubmitCode